import React, { useState } from 'react';
import { Input, Button, Collapse, Space } from 'antd';
import { DeleteOutlined, InfoCircleOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import VariantPricingMode from './VariantPricingMode';
import VariantTagInput from './VariantTagInput';
import BulkOperations from './BulkOperations';
import VariantCard from './VariantCard';

const VariantGroupCard = ({ group, groupIndex, onUpdate, onDelete }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const handleGroupNameChange = (e) => {
        onUpdate({ ...group, name: e.target.value });
    };

    const handlePricingModeChange = (autoCalculate) => {
        onUpdate({ ...group, autoCalculate });
    };

    const handleAddVariantValue = (value) => {
        const newVariant = {
            value,
            mrp: null,
            price: null,
            stock: null,
            discount: 0,
            imageUrl: '',
            isActive: true
        };
        const newVariants = [...(group.variants || []), newVariant];
        onUpdate({ ...group, variants: newVariants });
    };

    const handleUpdateVariant = (variantIndex, updatedVariant) => {
        const newVariants = [...(group.variants || [])];
        newVariants[variantIndex] = updatedVariant;
        onUpdate({ ...group, variants: newVariants });
    };

    const handleDeleteVariant = (variantIndex) => {
        const newVariants = [...(group.variants || [])];
        newVariants.splice(variantIndex, 1);
        onUpdate({ ...group, variants: newVariants });
    };

    const handleBulkApply = (updates) => {
        const newVariants = (group.variants || []).map(v => ({ ...v, ...updates }));
        onUpdate({ ...group, variants: newVariants });
    };

    const variants = group.variants || [];

    return (
        <div style={{
            background: '#f8fafc',
            border: '1px solid #cbd5e1',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
        }}>
            {/* Header: Group Name & Delete */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ cursor: 'pointer', padding: '4px' }} onClick={() => setIsExpanded(!isExpanded)}>
                        {isExpanded ? <DownOutlined style={{ color: '#64748b' }} /> : <RightOutlined style={{ color: '#64748b' }} />}
                    </div>
                    <div>
                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Variant Group Name</div>
                        <Input 
                            placeholder="e.g. Color, Size" 
                            value={group.name} 
                            onChange={handleGroupNameChange} 
                            style={{ width: '220px', fontWeight: 700, borderRadius: '8px' }} 
                        />
                    </div>
                    <VariantPricingMode value={group.autoCalculate !== false} onChange={handlePricingModeChange} />
                </div>
                <Button type="text" danger icon={<DeleteOutlined />} onClick={onDelete} style={{ fontWeight: 600 }}>Remove Group</Button>
            </div>

            {/* Auto Margin Helper */}
            {group.autoCalculate !== false && (
                <div style={{ marginBottom: '16px', padding: '8px 12px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: '8px', color: '#1d4ed8', fontSize: '12px', fontWeight: 600 }}>
                    <InfoCircleOutlined /> 
                    Margin locked based on the first variant. Selling prices for subsequent variants will be generated automatically.
                </div>
            )}

            {isExpanded && (
                <>
                    {/* Add Variant Tags & Bulk Operations */}
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginBottom: '16px' }}>
                        <VariantTagInput 
                            existingValues={variants.map(v => v.value)} 
                            onAddValue={handleAddVariantValue} 
                        />
                        {variants.length > 0 && <BulkOperations onApply={handleBulkApply} />}
                    </div>

                    {/* Variant Cards */}
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {variants.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', background: '#fff', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                                No variant values added yet. Add a value above to begin.
                            </div>
                        ) : (
                            variants.map((v, idx) => (
                                <VariantCard 
                                    key={idx}
                                    variant={v}
                                    isAutoCalculate={group.autoCalculate !== false}
                                    isBaseVariant={idx === 0}
                                    onUpdate={(updatedV) => handleUpdateVariant(idx, updatedV)}
                                    onDelete={() => handleDeleteVariant(idx)}
                                />
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default React.memo(VariantGroupCard);
